import { CampaignTypeFilter } from 'modules/domains/campaign';
import { MESSAGE_MEDIUMS_TO_CAMPAIGN_TYPES, MetricType } from '../components/constants';
import {
  AlertStatus,
  DEFAULT_FREQUENCY_FORM_VALUE,
  DEFAULT_STATUS_FORM_VALUE,
  DEFAULT_THRESHOLD_FORM_VALUE,
} from '../constants';
import {
  AlertMetric,
  AlertMetricsByMedium,
  AllMessageMediums,
  CampaignAlert,
  SavedAlertsByMedium,
} from '../types';
import { parseHourDurationToFrequency } from '../utils';

type Props = {
  savedAlert: CampaignAlert | undefined;
  medium: AllMessageMediums;
  type: CampaignTypeFilter;
  metricName: string;
  valueType: AlertMetric['valueType'];
};

const getIsAlertEnabled = (status: AlertStatus | undefined) => status === AlertStatus.Enabled;

export const getAlertValues = ({ savedAlert, medium, type, metricName, valueType }: Props) => {
  const nameBase = `${medium}-${type}-${metricName}`;
  const status = savedAlert?.status;

  return status
    ? // alert exists and is either enabled or disabled
      {
        [nameBase]: {
          status: getIsAlertEnabled(status),
          ...(valueType === MetricType.Number && { numberThreshold: `${savedAlert?.threshold}` }),
          ...(valueType === MetricType.Percentage && {
            percentageThreshold: `${savedAlert?.threshold}`,
          }),
          frequency: parseHourDurationToFrequency(savedAlert?.frequency),
        },
      }
    : // alert has not been created/saved before
      {
        [nameBase]: {
          status: DEFAULT_STATUS_FORM_VALUE,
          ...(valueType === MetricType.Number && { numberThreshold: DEFAULT_THRESHOLD_FORM_VALUE }),
          ...(valueType === MetricType.Percentage && {
            percentageThreshold: DEFAULT_THRESHOLD_FORM_VALUE,
          }),
          frequency: DEFAULT_FREQUENCY_FORM_VALUE,
        },
      };
};

/**
 *
 * Utilizes all supported alert metrics and existing saved alerts to
 * generate the initial values for the campaign alert defaults form.
 *
 * @param allAlertMetrics
 * @param savedAlerts
 * @returns initial alert values with defaults used for any alerts
 * that have not been previously saved
 *
 * Return data tentatively structured as:
 * {
 *  [messageMedium-campaignType-metricName]: {
 *      status,
 *      threshold,
 *      frequency,
 *  }
 *  ...
 * }
 */
export const getFormInitialValues = (
  allAlertMetrics: AlertMetricsByMedium,
  savedAlerts: SavedAlertsByMedium,
) =>
  MESSAGE_MEDIUMS_TO_CAMPAIGN_TYPES.reduce((acc, curr) => {
    const { medium, types } = curr;

    const allAlertsForAMedium = allAlertMetrics[medium]?.reduce(
      (acc2, { metricName, valueType }) => {
        const allEntriesForAMetricType = types.reduce((acc3, { type }) => {
          const savedAlert = savedAlerts[medium]?.find(
            (metric: CampaignAlert) =>
              metric.metricName === metricName && metric.campaignType === type,
          );
          const alert = getAlertValues({ savedAlert, medium, type, metricName, valueType });

          return { ...acc3, ...alert };
        }, {});

        return { ...acc2, ...allEntriesForAMetricType };
      },
      {},
    );

    return { ...acc, ...allAlertsForAMedium };
  }, {});
